import memoize from 'memoize-one';
import type { ReactNode } from 'react';
import { useContext, useEffect, useMemo, useRef } from 'react';

import type { IntlShape } from 'react-intl';
import type { RovoSkillsInsertMenuConfig } from '@atlassian/rovo-skills/insert-menu-provider/types';
import { useIntl } from 'react-intl';

import type { AnalyticsReactContextInterface } from '@atlaskit/analytics-next/AnalyticsReactContext';
import AnalyticsReactContext from '@atlaskit/analytics-next/AnalyticsReactContext';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions/extension-handler';
import type { ExtensionManifest } from '@atlaskit/editor-common/extensions/extension-manifest';
import type { ExtensionProvider } from '@atlaskit/editor-common/extensions/extension-provider';
import DefaultExtensionProvider from '@atlaskit/editor-common/extensions/default-extension-provider';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import type { EditorContentMode } from '@atlaskit/editor-common/types/editor-appearance';
import dropboxExtension from '@atlaskit/editor-extension-dropbox/manifest';
import type FeatureFlagClient from '@atlaskit/feature-flag-client/client';
import type { MentionProvider } from '@atlaskit/mention/types';

import { manifest as linkCreateExtension } from '@atlassian/editor-extension-link-create/manifest';
import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { createManifest as createNativeEmbedManifest } from '@atlassian/native-embeds-editor-extension/manifest';
import { createMauiNativeEmbedBannerRenderer } from '@atlassian/editor-plugin-malleable-ui/ui/maui-embed';
import { createNativeEmbedsFallbackManifest } from '@atlassian/native-embeds-fallback-editor-extension/main';

import {
	expVal,
	expValEquals,
	isExperimentEnabled,
	UNSAFE_expValNoExposure,
} from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	connectChartExtensionProvider,
	referentialityExtensionProvider,
} from '@confluence/app-referentiality/entry-points/providers.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useSmartButtonPremiumGate } from '@confluence/automation-extensions/entry-points/useSmartButtonPremiumGate.ts';
import { smartButtonExtensionProvider } from '@confluence/automation-extensions/entry-points/SmartButtonManifest.ts';
import { snippetExtensionProvider } from '@confluence/snippets/entry-points/snippetExtensionProvider.ts';
import { getSnippetsStateSnapshot } from '@confluence/snippets/entry-points/getSnippetsStateSnapshot.ts';
import { registerSnippetsQuickInsertOnLoad } from '@confluence/snippets/entry-points/registerSnippetsQuickInsertOnLoad.ts';
import type { Snippet } from '@confluence/snippets/entry-points/snippetType.ts';
import type { ExtensionSource } from '@confluence/change-edition/entry-points/PremiumExtensionsUpsell.tsx';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { projectLinkerExtensionProvider } from '@confluence/create-space/entry-points/projectLinkerExtensionProvider.ts';
import { createPremiumExtensionManifests } from '@confluence/custom-sites-extensions/entry-points/createPremiumExtensionManifests.ts';
import { usePremiumGate } from '@confluence/custom-sites-extensions/entry-points/usePremiumGate.ts';
import type { SubscriptionHandlerFnType } from '@confluence/editor-features/entry-points/SubscriptionHandlerFnType.ts';
import { DROPBOX_APP_KEY } from '@confluence/editor-features/entry-points/DROPBOX_APP_KEY.ts';
import { useDocChangeContext } from '@confluence/editor-features/entry-points/useDocChangeContext.ts';
import { ClassicEditorContext } from '@confluence/editor-features/entry-points/ClassicEditorContext.ts';
import { getOptedOutMacros } from '@confluence/editor-features/entry-points/featureFlagsUtils.ts';
import { createThirdPartyQuickInsertExtensionProvider } from '@confluence/editor-features/entry-points/thirdPartyQuickInsertExtensionProvider.ts';
import { useConnectedThirdPartyProductKeys } from '@confluence/editor-features/entry-points/useConnectedThirdPartyProductKeys.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { useShouldHideOverlay } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/useShouldHideOverlay.ts';
import type { ConfluencePageContext } from '@confluence/fabric-extension-lib/entry-points/ConfluencePageContext.ts';
import { getConfluenceMacrosExtensionProviderForEditor } from '@confluence/fabric-extension-lib/entry-points/getConfluenceMacrosExtensionProviderForEditor.ts';
import type { OnCompleteCallbackFnType } from '@confluence/fabric-extension-lib/entry-points/extensionTypes.ts';
import type { MacroConfig } from '@confluence/fabric-extension-lib/entry-points/extension-provider.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useIsAdmin } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useGetContextToken } from '@confluence/forge-ui/entry-points/useContextToken.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import {
	useRouteActions,
	type RouteStateType,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	setInEditorTemplatesPanelQueryParam,
	IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN,
	CONTEXT_PANEL_TAB_TARGET_BLOCK,
} from '@confluence/template-utils/entry-points/urlUtils.ts';
import { useEmbeddedEditPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/edit-page.ts';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';

import { getFullPageExtensionHandlers } from './FullPageMacroExtensionHandler.tsx';
import { getForgeExtensionHandler } from './getForgeExtensionHandler.ts';
import { type EditorAPI } from './extensionHandlerTypes.ts';

import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import type { ExtensionConfig, EditorProps } from '@atlaskit/editor-core/editor';

type UseExtensionOptionsProps = {
	contentId: string;
	contentType: string;
	contentTitle?: string;
	contentVersion?: number;
	editorActions: EditorActions;
	mentionProvider: Promise<MentionProvider>;
	spaceKey: string;
	spaceId?: string;
	contentMode?: EditorContentMode;
	extensionContextProvider?: {
		ClassicEditorContextProvider: any;
	};
	openConnectionsPanel?: (localId: string) => void;
	openConfigPanel?: (localId: string) => void;
	openSmartButtonConfigPanel?: (localId: string) => void;
	onCompleteCallback?: OnCompleteCallbackFnType;
	isLivePage?: boolean;
	isLivePageViewMode?: boolean;
	allowCreateDatabaseExtension?: boolean;
	allowDatabaseQuickInsert?: boolean;
	allowCreateWhiteboardExtension?: boolean;
	rovoSkillsConfig?: RovoSkillsInsertMenuConfig;
	flags?: FlagsStateContainer;
	aiOptInStatus?: 'enabled' | 'disabled-opt-in' | 'disabled';
	editorAPI: EditorAPI | undefined;
	setUpsellModalSource?: (source: ExtensionSource) => void;
	isFullPageEditor?: boolean;
	allowLegacyMacros?: boolean;
	allowCompanyHubExclusiveExtensions?: boolean;
	isCollabEditForLiveUpdatesEnabled?: boolean;
	pageTreeStateUpdater?: PageTreeStateUpdater;
	getHeadingLinkPath?: () => string | null;
};

const noop = () => {};
const getEmptyMigrationExtensionManifest = () => [];

const _getClassicEditorProviders = memoize(
	({
		cloudId,
		contentId,
		contentTitle,
		contentType,
		contentVersion,
		spaceKey,
		spaceId,
		contentMode,
		editorActions,
		createAnalyticsEvent,
		mentionProvider,
		intlNext,
		openMacroBrowserToEditExistingMacro,
		openMacroBrowserForInitialConfiguration,
		setActiveModal,
		openConnectionsPanel,
		openConfigPanel,
		openSmartButtonConfigPanel,
		closeActiveModal,
		getMigrationExtensionManifest,
		extensionContextProvider,
		subscribeDocChange,
		unsubscribeDocChange,
		onCompleteCallback,
		analyticsContext,
		isLivePage,
		isLivePageViewMode,
		allowCreateDatabaseExtension,
		allowDatabaseQuickInsert,
		allowCreateWhiteboardExtension,
		rovoSkillsConfig,
		flags,
		editorAPI,
		setUpsellModalSource,
		hideGlassPaneForForge,
		isFullPageEditor,
		allowLegacyMacros,
		allowCompanyHubExclusiveExtensions,
		isCollabEditForLiveUpdatesEnabled,
		setQueryParams,
		isBlockTemplateQuickInsertEnabled,
		snippets,
		pageTreeStateUpdater,
		experienceTracker,
		edition,
		isSmartButtonAllowed,
		isAdmin,
		userId,
		isPublishedPage,
		connectedProductKeysPromise,
		allowAgentManagedExtensionProvider,
		getHeadingLinkPath,
	}: {
		cloudId: string;
		activationId?: string;
		createAnalyticsEvent: CreateUIAnalyticsEvent;
		openMacroBrowserToEditExistingMacro?: Function;
		openMacroBrowserForInitialConfiguration?: Function;
		setActiveModal?: (component: ReactNode, props: any) => void;
		closeActiveModal?: () => void;
		getMigrationExtensionManifest?: Function;
		intlNext: IntlShape;
		featureFlagClient: FeatureFlagClient;
		subscribeDocChange: SubscriptionHandlerFnType;
		unsubscribeDocChange: SubscriptionHandlerFnType;
		analyticsContext: AnalyticsReactContextInterface;
		hideGlassPaneForForge: boolean;
		isLivePage?: boolean;
		isLivePageViewMode?: boolean;
		isCollabEditForLiveUpdatesEnabled?: boolean;
		setQueryParams: RouteStateType['setQueryParams'];
		isBlockTemplateQuickInsertEnabled: boolean;
		snippets: Snippet[];
		experienceTracker: ExperienceTrackerAPI;
		edition: ConfluenceEdition | null;
		isSmartButtonAllowed: boolean;
		isAdmin: boolean;
		userId: string | null;
		contentVersion?: number;
		isPublishedPage: boolean;
		connectedProductKeysPromise?: Promise<Set<string> | undefined>;
		allowAgentManagedExtensionProvider: boolean;
	} & UseExtensionOptionsProps) => {
		const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
		const pageContext: ConfluencePageContext = {
			contentId,
			requestingUserId: userId,
			contentTitle,
			spaceKey,
			spaceId,
			features: {
				configPanel: {
					enabled: true,
					optedOut: getOptedOutMacros(),
				},
				elementBrowser: true,
			},
		};

		const extensionHandlers = getFullPageExtensionHandlers({
			contentId,
			contentType,
			spaceKey,
			extensionContextProvider,
			editorActions,
			subscribeDocChange,
			unsubscribeDocChange,
			isLivePage,
			isCollabEditForLiveUpdatesEnabled,
			getHeadingLinkPath,
		});

		const macroConfig: MacroConfig = {
			contentId,
			setActiveModal,
			closeActiveModal,
		};

		const hideCreateContentMacros = isCompanyHub;
		const confluenceMacroExtensionProviderForEditor = getConfluenceMacrosExtensionProviderForEditor(
			pageContext,
			extensionHandlers,
			editorAPI ?? undefined,
			editorActions,
			macroConfig,
			// Embedded editors may lack classic context; concat needs an empty list, not undefined.
			getMigrationExtensionManifest ?? getEmptyMigrationExtensionManifest,
			openMacroBrowserToEditExistingMacro || noop,
			openMacroBrowserForInitialConfiguration || noop,
			intlNext,
			createAnalyticsEvent,
			onCompleteCallback,
			isLivePage,
			flags,
			experienceTracker,
			contentType,
			allowCreateDatabaseExtension && !hideCreateContentMacros,
			allowDatabaseQuickInsert,
			allowCreateWhiteboardExtension && !hideCreateContentMacros,
			rovoSkillsConfig,
			allowLegacyMacros,
			isCompanyHub,
			pageTreeStateUpdater,
			allowAgentManagedExtensionProvider,
			isLivePageViewMode,
		);

		const extensionProvidersList: (Promise<ExtensionProvider> | ExtensionProvider)[] = [];
		extensionProvidersList.push(confluenceMacroExtensionProviderForEditor);

		const dropboxManifest = dropboxExtension({
			appKey: DROPBOX_APP_KEY,
			canMountinIframe: false,
		});
		const extensions = [] as ExtensionManifest[];
		extensions.push(
			isExperimentEnabled('platform_editor_slash_command')
				? {
						...dropboxManifest,
						modules: {
							...dropboxManifest.modules,
							quickInsert: dropboxManifest.modules.quickInsert?.map((module) => ({
								...module,
								category: 'embed',
								priority: -500,
							})),
						},
					}
				: dropboxManifest,
		);

		if (!hideCreateContentMacros) {
			extensions.push(
				linkCreateExtension({
					intl: intlNext,
					defaultCloudId: cloudId,
					defaultFormValues: {
						'confluence-page': {
							parentId: contentId,
							spaceId,
						},
					},
					analyticsContext,
					pageContext: {
						isLivePage,
						isBlogpost: contentType === 'blogpost',
						isFullPageEditor,
					},
					location: 'confluence-page',
					allowConfluenceEditViewEmbed: true,
					flags: {
						showSuccess: ({ title, description, actions }) => {
							void flags?.showSuccessFlag({
								title,
								description,
								actions,
								isAutoDismiss: true,
							});
						},
						showFailure: ({ title, description, actions }) => {
							void flags?.showErrorFlag({
								title,
								description,
								actions,
								isAutoDismiss: true,
							});
						},
					},
				}),
			);
		}

		extensionProvidersList.push(new DefaultExtensionProvider(extensions));

		const isPremium = edition === ConfluenceEdition.PREMIUM;
		const showPremiumExtensions = isAdmin || isPremium;

		const premiumExtensions = showPremiumExtensions
			? createPremiumExtensionManifests({
					contentId,
					editorActions,
					openConfigPanel,
					intl: intlNext,
					setUpsellModalSource,
					allowCompanyHubExclusiveExtensions,
				})
			: [];

		extensionProvidersList.push(new DefaultExtensionProvider(premiumExtensions));

		// Don't add the Smart Button extension if we're in the Company Hub,
		// the company-hub-migration-prevent-1p-macro experiment is enabled,
		// and the company-hub-prevent-1p-macro-via-tap-trait gate returns false.
		// company-hub-prevent-1p-macro-via-tap-trait is controlled by a tap trait that checks if a tenant's company hub
		// has been published with unsupported 1p macros in the last 4 months,
		// in which case we will allow them to insert Smart Button, an unsupported 1p macro.
		if (
			!isCompanyHub ||
			!expVal('company-hub-migration-prevent-1p-macro', 'isEnabled', false) ||
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			fg('company-hub-prevent-1p-macro-via-tap-trait')
		) {
			extensionProvidersList.push(
				smartButtonExtensionProvider({
					contentId,
					editorActions,
					openSmartButtonConfigPanel,
					intl: intlNext,
					isSmartButtonAllowed,
					contentType,
				}),
			);
		}

		extensionProvidersList.push(
			projectLinkerExtensionProvider({
				cloudId,
				intl: intlNext,
				editorActions,
				createAnalyticsEvent,
			}),
		);

		extensionProvidersList.push(
			connectChartExtensionProvider(
				intlNext,
				editorActions,
				openConnectionsPanel,
				createAnalyticsEvent,
			),
		);

		extensionProvidersList.push(
			referentialityExtensionProvider({
				intl: intlNext,
				openConnectionsPanel,
				createAnalyticsEvent,
			}),
		);

		if (
			UNSAFE_expValNoExposure('cc-maui-experiment', 'isEnabled', false) ||
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			fg('platform_native_embeds_rollout_non_maui_experience') ||
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Shared Platform native embed rollout.
			fg('platform_native_embeds_rollout_all_products')
		) {
			const pageMode = isLivePage ? 'live' : 'edit';
			const analyticsPageMode: 'view' | 'edit' | 'live' = isPublishedPage ? 'view' : pageMode;
			const nativeEmbedAnalyticsPageContext = {
				contentId,
				spaceId,
				isLivePage: !!isLivePage,
				pageMode: analyticsPageMode,
			};
			const nativeEmbedExtension = createNativeEmbedManifest(
				isPublishedPage,
				editorAPI ?? undefined,
				undefined,
				createMauiNativeEmbedBannerRenderer(editorAPI ?? undefined),
				nativeEmbedAnalyticsPageContext,
			);
			extensionProvidersList.push(
				new DefaultExtensionProvider(
					[nativeEmbedExtension],
					nativeEmbedExtension.modules?.autoConvert?.url ?? [],
				),
			);
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		} else if (fg('platform_editor_native_embeds_fallback_transform')) {
			extensionProvidersList.push(
				new DefaultExtensionProvider([createNativeEmbedsFallbackManifest(editorAPI ?? undefined)]),
			);
		}

		if (
			!isFullPageEditor ||
			!expValEquals('platform_editor_snippets_plugin_confluence', 'isEnabled', true)
		) {
			const shouldAddBlockTemplateQuickInsert = isBlockTemplateQuickInsertEnabled;
			const onOpenTemplatePanel = () => {
				if (setQueryParams) {
					setInEditorTemplatesPanelQueryParam(
						setQueryParams,
						IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN,
						CONTEXT_PANEL_TAB_TARGET_BLOCK,
					);
				}
			};
			extensionProvidersList.push(
				snippetExtensionProvider(
					intlNext,
					snippets,
					shouldAddBlockTemplateQuickInsert ? onOpenTemplatePanel : undefined,
					userId,
					createAnalyticsEvent,
				),
			);
		}

		extensionProvidersList.push(
			createThirdPartyQuickInsertExtensionProvider({
				editorActions,
				intlNext,
				createAnalyticsEvent,
				connectedProductKeysPromise,
			}),
		);

		extensionProvidersList.push(
			getForgeExtensionHandler({
				spaceKey,
				spaceId,
				contentId,
				contentType,
				contentVersion,
				contentMode,
				mentionProvider,
				isEditing: true,
				connectExtensionProvider: confluenceMacroExtensionProviderForEditor,
				editorActions,
				hideGlassPaneForForge,
				intl: intlNext,
				locale: intlNext.locale,
				isLivePage: !!isLivePage,
				editorAPI,
				useGetContextToken,
				productExtensionHandlers: expValEquals(
					'confluence_forge_nesting_native_macro_livepage',
					'isEnabled',
					true,
				)
					? extensionHandlers
					: undefined,
			}),
		);

		return extensionProvidersList;
	},
);

const _getExtensionRelatedProps = memoize(
	({
		cloudId,
		contentId,
		contentTitle,
		contentType,
		contentVersion,
		contentMode,
		editorActions,
		createAnalyticsEvent,
		mentionProvider,
		spaceKey,
		spaceId,
		// Props from ClassicEditorContext
		openMacroBrowserToEditExistingMacro,
		openMacroBrowserForInitialConfiguration,
		setActiveModal,
		openConnectionsPanel,
		openConfigPanel,
		openSmartButtonConfigPanel,
		closeActiveModal,
		getMigrationExtensionManifest,
		extensionContextProvider,
		intlNext,
		featureFlagClient,
		subscribeDocChange,
		unsubscribeDocChange,
		onCompleteCallback,
		analyticsContext,
		isLivePage,
		isLivePageViewMode,
		allowCreateDatabaseExtension,
		allowDatabaseQuickInsert,
		allowCreateWhiteboardExtension,
		rovoSkillsConfig,
		flags,
		aiOptInStatus,
		editorAPI,
		setUpsellModalSource,
		hideGlassPaneForForge,
		isFullPageEditor,
		allowLegacyMacros,
		allowCompanyHubExclusiveExtensions,
		isCollabEditForLiveUpdatesEnabled,
		setQueryParams,
		isBlockTemplateQuickInsertEnabled,
		snippets,
		pageTreeStateUpdater,
		experienceTracker,
		edition,
		isSmartButtonAllowed,
		isAdmin,
		userId,
		isPublishedPage,
		connectedProductKeysPromise,
		allowAgentManagedExtensionProvider,
		getHeadingLinkPath,
	}: Parameters<typeof _getClassicEditorProviders>[0]) => {
		const extensionProviders = _getClassicEditorProviders({
			cloudId,
			contentId,
			contentTitle,
			contentType,
			contentVersion,
			contentMode,
			spaceKey,
			spaceId,
			editorActions,
			createAnalyticsEvent,
			mentionProvider,
			intlNext,
			featureFlagClient,
			openMacroBrowserToEditExistingMacro,
			openMacroBrowserForInitialConfiguration,
			setActiveModal,
			openConnectionsPanel,
			openConfigPanel,
			openSmartButtonConfigPanel,
			closeActiveModal,
			getMigrationExtensionManifest,
			extensionContextProvider,
			subscribeDocChange,
			unsubscribeDocChange,
			onCompleteCallback,
			analyticsContext,
			isLivePage,
			isLivePageViewMode,
			allowCreateDatabaseExtension,
			allowDatabaseQuickInsert,
			allowCreateWhiteboardExtension,
			rovoSkillsConfig,
			flags,
			snippets,
			aiOptInStatus,
			editorAPI,
			setUpsellModalSource,
			hideGlassPaneForForge,
			isFullPageEditor,
			allowLegacyMacros,
			allowCompanyHubExclusiveExtensions,
			isCollabEditForLiveUpdatesEnabled,
			setQueryParams,
			isBlockTemplateQuickInsertEnabled,
			pageTreeStateUpdater,
			experienceTracker,
			edition,
			isSmartButtonAllowed,
			isAdmin,
			userId,
			isPublishedPage,
			connectedProductKeysPromise,
			allowAgentManagedExtensionProvider,
			getHeadingLinkPath,
		});

		const allowExtension: EditorProps['allowExtension'] = {
			allowBreakout: true,
			allowExtendFloatingToolbars: true,
		};

		return {
			allowExtension,
			extensionProviders,
		};
	},
);

export const useExtensionOptions = ({
	contentId,
	contentTitle,
	contentType,
	contentVersion,
	editorActions,
	mentionProvider,
	spaceKey,
	spaceId,
	contentMode,
	extensionContextProvider,
	openConnectionsPanel,
	openConfigPanel,
	openSmartButtonConfigPanel,
	onCompleteCallback,
	isLivePage,
	isLivePageViewMode,
	allowCreateDatabaseExtension,
	allowDatabaseQuickInsert,
	allowCreateWhiteboardExtension,
	rovoSkillsConfig,
	flags,
	aiOptInStatus,
	editorAPI,
	setUpsellModalSource,
	isFullPageEditor = false,
	allowLegacyMacros = false,
	allowCompanyHubExclusiveExtensions = false,
	isCollabEditForLiveUpdatesEnabled,
	pageTreeStateUpdater,
	getHeadingLinkPath,
}: UseExtensionOptionsProps): {
	extensionOptions: {
		allowExtension: ExtensionConfig;
		extensionProviders: (ExtensionProvider<any> | Promise<ExtensionProvider<any>>)[];
	};
	rendererExtensionOptions: {
		getExtensionHandlers: ((options: any) => ExtensionHandlers) | undefined;
		populateExtensionProviders:
			| ((dataProviders: ProviderFactory, options: any) => void)
			| undefined;
		getHeadingLinkPath?: () => string | null;
	};
} => {
	const {
		openMacroBrowserForInitialConfiguration,
		openMacroBrowserToEditExistingMacro,
		loadMacroBrowserResources,
		setActiveModal,
		closeActiveModal,
		getMigrationExtensionManifest,
		getExtensionHandlers,
		populateExtensionProviders,
	} = useContext(ClassicEditorContext);
	const { cloudId, featureFlagClient, edition, userId, activationId, isRovoLLMEnabled } =
		useSessionData();
	const { isSmartButtonAllowed } = useSmartButtonPremiumGate();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const isAdmin = useIsAdmin();
	const shouldShowPremiumUpsell = usePremiumGate();
	const intlNext = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setQueryParams } = useRouteActions();

	// Trigger loading of macro browser resources
	useEffect(() => {
		if (loadMacroBrowserResources) {
			loadMacroBrowserResources();
		}
	}, [loadMacroBrowserResources]);

	const { subscribeDocChange, unsubscribeDocChange } = useDocChangeContext();

	const analyticsContext = useContext(AnalyticsReactContext);

	const { isTemplateBrowserEnabled } = useEmbeddedEditPageFeatures();
	const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();

	// Enable block template quick insert when we are not in embedded Confluence or when in embedded Confluence andthe template browser is enabled
	const isBlockTemplateQuickInsertEnabled = !isEmbeddedConfluence || !!isTemplateBrowserEnabled;
	const isSnippetsPluginMigrationActive = Boolean(
		isFullPageEditor &&
		expValEquals('platform_editor_snippets_plugin_confluence', 'isEnabled', true),
	);

	// Get snippets snapshot without subscribing to avoid re-renders
	const {
		snippets,
		isLoading: snippetsLoading,
		isLoaded: snippetsLoaded,
	} = getSnippetsStateSnapshot();
	// If snippets are still loading, register a callback to add them to quick insert when done.
	// Only register when quick insert is enabled (not gated off in Embedded Confluence).
	useEffect(() => {
		if (
			isBlockTemplateQuickInsertEnabled &&
			snippetsLoading &&
			!snippetsLoaded &&
			editorAPI &&
			!isSnippetsPluginMigrationActive
		) {
			registerSnippetsQuickInsertOnLoad(editorAPI, userId);
		}
		// Only watch editorAPI - we check the loading state from the snapshot at render time
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editorAPI]);

	// TODO: Currently, Forge macros can only be non-bodied, but there's work
	// underway to allow for bodied macros. When that's complete, we'll need
	// to update this to be contingent on the macro type.
	const hideGlassPaneForForge = useShouldHideOverlay('forge', false);

	const pageMode = useGetPageMode();
	const isPublishedPage = pageMode === PageMode.VIEW;

	// Third-party quick-insert items are filtered to products the user has actually
	// connected.
	const connectedProductKeys = useConnectedThirdPartyProductKeys();

	// Bridge the async connected-product keys into a single stable promise.
	// `connectedProductKeys` transitions from `undefined` to a `Set` once the
	// connector-config request resolves; feeding that value straight into the
	// extension-provider memo would rebuild every provider and force the editor to
	// re-initialise mid-load. Instead we hand the third-party provider a promise
	// with a stable identity so the editor keeps the same `extensionProviders`
	// array and just picks up the quick-insert items when the request settles.
	const connectedProductKeysDeferredRef = useRef<{
		promise: Promise<Set<string> | undefined>;
		resolve: (value: Set<string> | undefined) => void;
	} | null>(null);
	if (connectedProductKeysDeferredRef.current === null) {
		let resolveConnectedProductKeys: (value: Set<string> | undefined) => void = () => {};
		const promise = new Promise<Set<string> | undefined>((resolve) => {
			resolveConnectedProductKeys = resolve;
		});
		connectedProductKeysDeferredRef.current = { promise, resolve: resolveConnectedProductKeys };
	}
	useEffect(() => {
		if (connectedProductKeys !== undefined) {
			connectedProductKeysDeferredRef.current?.resolve(connectedProductKeys);
		}
	}, [connectedProductKeys]);
	const connectedProductKeysPromise = connectedProductKeysDeferredRef.current.promise;

	const extensionOptions = useMemo(
		() =>
			_getExtensionRelatedProps({
				cloudId,
				activationId,
				contentId,
				contentTitle,
				contentType,
				contentVersion,
				contentMode,
				editorActions,
				createAnalyticsEvent,
				mentionProvider,
				spaceKey,
				spaceId,
				openMacroBrowserForInitialConfiguration,
				openMacroBrowserToEditExistingMacro,
				setActiveModal,
				openConnectionsPanel,
				openConfigPanel,
				openSmartButtonConfigPanel,
				closeActiveModal,
				getMigrationExtensionManifest,
				extensionContextProvider,
				intlNext,
				featureFlagClient,
				subscribeDocChange,
				unsubscribeDocChange,
				onCompleteCallback,
				analyticsContext,
				isLivePage,
				isLivePageViewMode,
				allowCreateDatabaseExtension,
				allowDatabaseQuickInsert,
				allowCreateWhiteboardExtension,
				rovoSkillsConfig,
				flags,
				aiOptInStatus,
				editorAPI,
				setUpsellModalSource: shouldShowPremiumUpsell ? setUpsellModalSource : undefined,
				hideGlassPaneForForge,
				isFullPageEditor,
				allowLegacyMacros,
				allowCompanyHubExclusiveExtensions,
				isCollabEditForLiveUpdatesEnabled,
				setQueryParams,
				isBlockTemplateQuickInsertEnabled,
				snippets,
				pageTreeStateUpdater,
				experienceTracker,
				edition,
				isSmartButtonAllowed,
				isAdmin,
				userId,
				isPublishedPage,
				connectedProductKeysPromise,
				allowAgentManagedExtensionProvider: isRovoLLMEnabled,
				getHeadingLinkPath,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			contentId,
			contentVersion,
			userId,
			spaceKey,
			// Rebuild providers when live pages switch between edit and view manifests.
			isLivePageViewMode,
			editorAPI,
			spaceId,
			// `connectedProductKeysPromise` has a stable identity (see the deferred ref
			// above), so it is intentionally omitted here — the third-party provider is
			// built once and resolves its manifest when the connector config settles,
			// which avoids rebuilding the editor's extension providers mid-load.
			isRovoLLMEnabled,
			pageTreeStateUpdater,
			getHeadingLinkPath,
		],
	);

	return {
		extensionOptions,
		rendererExtensionOptions: {
			getExtensionHandlers,
			populateExtensionProviders,
			getHeadingLinkPath,
		},
	};
};
